import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchUsers,
  fetchCompanies,
  fetchRoles,
  setCurrentPage,
  updateUser,
  deleteUser,
  setRoles,
} from "../redux/slices/companyRoleSlice";

import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import SuccessModal from "../components/SuccessModal";
import { Modal, Button } from "react-bootstrap";
import api from "../api/axios";
import { toast } from "react-toastify";
import {
  PRIVILEGED_ROLES,
  RETIRED_ROLES,
  SUPERADMIN_ONLY_LABEL,
  roleKey,
} from "../constants/roles";
import { isSuperadminUser } from "../utils/leadershipRoles";
import { superadminErrorMessage, useCurrentUser } from "../utilities/currentUser";

const CompanyRolePage = ({ onLogout }) => {
  const dispatch = useDispatch();
  const { user: currentUser } = useCurrentUser();
  // Creating/deleting roles and granting or removing Admin/Superadmin are Superadmin-only.
  const isSuperadmin = isSuperadminUser(currentUser);
  const isPrivilegedRole = (role) =>
    PRIVILEGED_ROLES.some((name) => roleKey(name) === roleKey(role?.name));
  // Retired roles (Admin) are never offered; the API hides and refuses them too.
  const isRetiredRole = (role) =>
    RETIRED_ROLES.some((name) => roleKey(name) === roleKey(role?.name));
  const { users, companies, roles, currentPage, totalPages } = useSelector(
    (state) => state.companyRole,
  );

  const [expandedCompany, setExpandedCompany] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState({});
  const [customRoles, setCustomRoles] = useState({});
  const [showUserDeleteModal, setShowUserDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showRoleDeleteModal, setShowRoleDeleteModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [editUserData, setEditUserData] = useState({
    name: "",
    email: "",
    department: "",
    designation: "",
    mobile_number: "",
    voter_id: "",
  });

  // Google user
  const googleUser = JSON.parse(localStorage.getItem("googleUser"));

  // Load users
  useEffect(() => {
    dispatch(fetchUsers({ page: currentPage }));
  }, [dispatch, currentPage]);

  // Load companies & roles
  useEffect(() => {
    dispatch(fetchCompanies()).then((res) => {
      res.payload.forEach((c) => {
        console.log("dispatch companies", res);

        dispatch(fetchRoles({ companyId: c.id, companyName: c.name }));
      });
    });
  }, [dispatch]);

  // Toggle Active/Inactive
  const handleToggleStatus = async (u) => {
    const newStatus = u.status === "active" ? "inactive" : "active";
    try {
      await api.post(`/api/users/${u.id}/toggle-status`, { status: newStatus });

      // Redux state update
      dispatch(
        updateUser({
          id: u.id,
          userData: { ...u, status: newStatus },
        }),
      );

      toast.success(
        `User ${
          newStatus === "active" ? "activated" : "deactivated"
        } successfully!`,
      );
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to update status!");
    }
  };

  // Accordion toggle
  const toggleAccordion = (id) =>
    setExpandedCompany(expandedCompany === id ? null : id);

  // Open modals
  const openRoleModal = (u) => {
    setSelectedUser(u);
    setSelectedCompanies({});
    setShowRoleModal(true);
  };

  const openEditModal = (u) => {
    setEditUserData({
      name: u.name,
      email: u.email,
      department: u.department,
      designation: u.designation,
      mobile_number: u.mobile_number,
      voter_id: u.voter_id,
    });
    setSelectedUser(u);
    setShowEditModal(true);
  };

  // Update user

  const handleUpdateUser = async () => {
    if (!editUserData.name || !editUserData.email) {
      toast.error("Name and Email are required!");
      return;
    }

    try {
      await dispatch(
        updateUser({ id: selectedUser.id, userData: editUserData }),
      ).unwrap();

      toast.success("User updated successfully!");
      setShowEditModal(false);
    } catch (err) {
      const msg =
        err?.errors?.email?.[0] || err?.message || "Failed to update user!";
      toast.error(msg);
    }
  };

  // Delete user
  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowUserDeleteModal(true);
  };

  const handleUserDeleteConfirm = async () => {
    if (!userToDelete) return;
    try {
      await dispatch(deleteUser(userToDelete.id)).unwrap();
      toast.success("User deleted successfully!");
      setShowUserDeleteModal(false);
      setUserToDelete(null);
    } catch (error) {
      toast.error(error?.message || (typeof error === "string" ? error : "Failed to delete user!"));
    } finally {
      setShowUserDeleteModal(false);
      setUserToDelete(null);
    }
  };

  // Company checkbox
  const handleCompanyChange = async (companyId, companyName, checked) => {
    if (!checked) {
      setSelectedCompanies((prev) => {
        const copy = { ...prev };
        delete copy[companyId];
        return copy;
      });
      return;
    }

    const res = await api.get(`/api/roles?company=${companyName}`);
    dispatch(setRoles({ companyId, roles: res.data }));
    setSelectedCompanies((prev) => ({ ...prev, [companyId]: [] }));
  };

  // Role checkbox
  const handleRoleChange = (companyId, roleId, checked) => {
    setSelectedCompanies((prev) => {
      const current = prev[companyId] || [];
      if (checked) return { ...prev, [companyId]: [...current, roleId] };
      return {
        ...prev,
        [companyId]: current.filter((r) => r !== roleId),
      };
    });
  };

  // Delete role
  const handleDeleteRole = (companyId, role) => {
    setRoleToDelete({ companyId, role });
    setShowRoleDeleteModal(true);
  };

  const handleRoleDeleteConfirm = async () => {
    if (!roleToDelete) return;
    const { companyId, role } = roleToDelete;
    try {
      await api.delete(`/api/roles/${role.id}`);
      dispatch(
        setRoles({
          companyId,
          roles: roles[companyId].filter((r) => r.id !== role.id),
        }),
      );
      setSelectedCompanies((prev) => ({
        ...prev,
        [companyId]: (prev[companyId] || []).filter((r) => r !== role.id),
      }));
      toast.success("Role deleted successfully!");
      setShowRoleDeleteModal(false);
      setRoleToDelete(null);
    } catch (err) {
      console.error(err);
      toast.error(superadminErrorMessage(err, "Error deleting role!"));
    } finally {
      setShowRoleDeleteModal(false);
      setRoleToDelete(null);
    }
  };

  // Add new role
  const handleAddRole = async (companyId) => {
    const roleName = customRoles[companyId]?.trim();
    if (!roleName) return alert("Enter a role name!");
    try {
      const res = await api.post("/api/roles", { name: roleName });
      const savedRole = res.data.role;
      dispatch(
        setRoles({
          companyId,
          roles: [...(roles[companyId] || []), savedRole],
        }),
      );
      setSelectedCompanies((prev) => ({
        ...prev,
        [companyId]: [...(prev[companyId] || []), savedRole.id],
      }));
      setCustomRoles((prev) => ({ ...prev, [companyId]: "" }));
      setSuccessMessage("Role added successfully!");
      setShowSuccessModal(true);
    } catch (err) {
      console.error(err);
      toast.error(superadminErrorMessage(err, "Error adding role!"));
    }
  };

  // Save roles for user
  const handleSaveRoles = async () => {
    if (!selectedUser?.id) return alert("User not found!");
    const payload = {
      user_id: selectedUser.id,
      companies: Object.entries(selectedCompanies).map(([cid, rids]) => ({
        company_id: parseInt(cid),
        roles: rids,
      })),
    };

    try {
      await api.post("/api/user-roles", payload);
      setSuccessMessage("Roles saved successfully!");
      setShowSuccessModal(true);
      setShowRoleModal(false);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Error saving roles!");
    }
  };

  return (
    <>
      <div className="container-fluid"> 
        <div className="wrapper">
          <div className="container mt-5">
            <h3 className="text-center mb-4 text-primary fw-bold">
              User Role Management
            </h3>

            {/*  User Table */}
            <div className="table-responsive">
              <table className="table table-striped table-bordered">
                <thead className="table-primary">
                  <tr>
                    <th>#</th>
                    <th>Image</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Department</th>
                    <th>Designation</th>
                    <th>Mobile</th>
                    <th>NID</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, i) => (
                    <tr key={u.id}>
                      <td>{i + 1}</td>
                      <td>
                        <img
                          src={
                            u.profile_photo_path
                              ? `${api.defaults.baseURL}/${u.profile_photo_path}`
                              : googleUser?.picture
                                ? `${googleUser.picture}`
                                : "assets/images/users/avatar-1.jpg"
                          }
                          // src={
                          //   u.profile_photo_path
                          //     ? u.profile_photo_path.startsWith("http")
                          //       ? u.profile_photo_path
                          //       : `https://api.iconicunitygroup.com/${u.profile_photo_path}`
                          //     : "assets/images/users/avatar-1.jpg"
                          // }
                          alt={u.name}
                          style={{
                            width: "50px",
                            height: "50px",
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      </td>
                      <td>{u.name}</td>
                      <td>{u.email}</td>
                      <td>{u.department}</td>
                      <td>{u.designation}</td>
                      <td>{u.mobile_number}</td>
                      <td>{u.voter_id}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => openRoleModal(u)}
                        >
                          Set Roles
                        </button>
                        <button
                          className="btn btn-sm btn-warning ms-2"
                          onClick={() => openEditModal(u)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger ms-2"
                          onClick={() => handleDeleteUser(u)}
                        >
                          Delete
                        </button>

                        {/* {u.status === "active" ? (
                          <button className="btn btn-sm btn-primary ms-2">
                            Active
                          </button>
                        ) : (
                          <button className="btn btn-sm btn-danger ms-2">
                            Inactive
                          </button>
                        )} */}

                        {/* active inactive button */}
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`statusSwitch-${u.id}`}
                            checked={u.status === "active"}
                            onChange={() => handleToggleStatus(u)}
                            style={{ cursor: "pointer" }}
                          />
                          <label
                            className="form-check-label ms-2"
                            htmlFor={`statusSwitch-${u.id}`}
                          >
                            {u.status === "active" ? "" : ""}
                          </label>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/*  Pagination */}
            <nav className="mt-3">
              <ul className="pagination justify-content-center">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => dispatch(setCurrentPage(currentPage - 1))}
                  >
                    Previous
                  </button>
                </li>
                {[...Array(totalPages)].map((_, i) => (
                  <li
                    key={i}
                    className={`page-item ${
                      currentPage === i + 1 ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => dispatch(setCurrentPage(i + 1))}
                    >
                      {i + 1}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => dispatch(setCurrentPage(currentPage + 1))}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>

            {/*  Role Modal with Add/Delete */}
            <Modal
              show={showRoleModal}
              onHide={() => setShowRoleModal(false)}
              size="lg"
              centered
            >
              {/*  */}
              <Modal.Header closeButton>
                <Modal.Title>
                  Manage Roles for <strong>{selectedUser?.name}</strong>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className="accordion" id="companyAccordion">
                  {companies.map((company) => (
                    <div className="accordion-item mb-3" key={company.id}>
                      <h2 className="accordion-header">
                        <button
                          className={`accordion-button ${
                            expandedCompany === company.id ? "" : "collapsed"
                          }`}
                          onClick={() => toggleAccordion(company.id)}
                        >
                          <div className="form-check">
                            <input
                              className="form-check-input me-2"
                              type="checkbox"
                              checked={
                                selectedCompanies[company.id] !== undefined
                              }
                              onChange={(e) =>
                                handleCompanyChange(
                                  company.id,
                                  company.name,
                                  e.target.checked,
                                )
                              }
                            />
                            {company.name}
                          </div>
                        </button>
                      </h2>

                      <div
                        className={`accordion-collapse collapse ${
                          expandedCompany === company.id ? "show" : ""
                        }`}
                      >
                        <div className="accordion-body">
                          {roles[company.id]?.filter((role) => !isRetiredRole(role)).map((role) => (
                            <div
                              key={role.id}
                              className="form-check mb-2 d-flex align-items-center justify-content-between"
                            >
                              <div>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  disabled={
                                    selectedCompanies[company.id] === undefined ||
                                    (isPrivilegedRole(role) && !isSuperadmin)
                                  }
                                  title={
                                    isPrivilegedRole(role) && !isSuperadmin
                                      ? SUPERADMIN_ONLY_LABEL
                                      : undefined
                                  }
                                  checked={
                                    selectedCompanies[company.id]?.includes(
                                      role.id,
                                    ) || false
                                  }
                                  onChange={(e) =>
                                    handleRoleChange(
                                      company.id,
                                      role.id,
                                      e.target.checked,
                                    )
                                  }
                                />
                                <label className="form-check-label ms-2">
                                  {role.name}
                                </label>
                                {isPrivilegedRole(role) && !isSuperadmin && (
                                  <small className="text-muted ms-2">
                                    ({SUPERADMIN_ONLY_LABEL})
                                  </small>
                                )}
                              </div>
                              <button
                                className="btn btn-sm btn-danger"
                                disabled={!isSuperadmin}
                                title={isSuperadmin ? undefined : SUPERADMIN_ONLY_LABEL}
                                onClick={() =>
                                  handleDeleteRole(company.id, role)
                                }
                              >
                                Delete
                              </button>
                            </div>
                          ))}

                          {/*  Add New Role */}
                          <div className="mt-3 border-top pt-3">
                            <h6 className="fw-semibold">
                              Add New Role
                              {!isSuperadmin && (
                                <small className="text-muted ms-2">
                                  ({SUPERADMIN_ONLY_LABEL})
                                </small>
                              )}
                            </h6>
                            <div className="input-group">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Enter role name"
                                value={customRoles[company.id] || ""}
                                onChange={(e) =>
                                  setCustomRoles({
                                    ...customRoles,
                                    [company.id]: e.target.value,
                                  })
                                }
                                disabled={
                                  !isSuperadmin ||
                                  selectedCompanies[company.id] === undefined
                                }
                              />
                              <button
                                className="btn btn-success"
                                onClick={() => handleAddRole(company.id)}
                                disabled={!isSuperadmin || !customRoles[company.id]}
                                title={isSuperadmin ? undefined : SUPERADMIN_ONLY_LABEL}
                              >
                                Add Role
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => setShowRoleModal(false)}
                >
                  Close
                </Button>
                <Button variant="primary" onClick={handleSaveRoles}>
                  Save Roles
                </Button>
              </Modal.Footer>
            </Modal>

            {/*  Edit User Modal */}
            <Modal
              show={showEditModal}
              onHide={() => setShowEditModal(false)}
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>Edit User Info</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <form>
                  {[
                    "name",
                    "email",
                    "department",
                    "designation",
                    "mobile_number",
                    "voter_id",
                  ].map((field, i) => (
                    <div className="mb-3" key={i}>
                      <label className="form-label text-capitalize">
                        {field.replace("_", " ")}
                      </label>
                      <input
                        type={field === "email" ? "email" : "text"}
                        className="form-control"
                        value={editUserData[field]}
                        onChange={(e) =>
                          setEditUserData({
                            ...editUserData,
                            [field]: e.target.value,
                          })
                        }
                      />
                    </div>
                  ))}
                </form>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleUpdateUser}>
                  Update
                </Button>
              </Modal.Footer>
            </Modal>

            {/* User Delete Confirmation Modal */}
            {showUserDeleteModal && userToDelete && (
              <DeleteConfirmationModal
                show={showUserDeleteModal}
                onClose={() => {
                  setShowUserDeleteModal(false);
                  setUserToDelete(null);
                }}
                onConfirm={handleUserDeleteConfirm}
                title="Delete User"
                message={`Are you sure you want to delete user "${userToDelete.name}"? This action cannot be undone.`}
              />
            )}

            {/* Role Delete Confirmation Modal */}
            {showRoleDeleteModal && roleToDelete && (
              <DeleteConfirmationModal
                show={showRoleDeleteModal}
                onClose={() => {
                  setShowRoleDeleteModal(false);
                  setRoleToDelete(null);
                }}
                onConfirm={handleRoleDeleteConfirm}
                title="Delete Role"
                message={`Are you sure you want to delete role "${roleToDelete.role.name}"? This action cannot be undone.`}
              />
            )}

            {/* Success Modal */}
            <SuccessModal
              show={showSuccessModal}
              onClose={() => setShowSuccessModal(false)}
              message={successMessage}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default CompanyRolePage;
